import { createClient } from '@supabase/supabase-js';
import type { Database } from '@/types/supabase';

// Get environment variables
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Validate environment variables
if (!supabaseUrl || !supabaseAnonKey) {
  console.error(
    'Missing Supabase environment variables. Please check your .env file.'
  );
}

// Create Supabase client
export const supabase = createClient<Database>(
  supabaseUrl as string,
  supabaseAnonKey as string,
  {
    auth: {
      persistSession: true,
      storageKey: 'secondopinion-auth',
    },
  }
);

// Helper function to get user admin status from user metadata
export async function getUserAdminStatus() {
  try {
    // Get the current user with full metadata
    const { data: { user }, error } = await supabase.auth.getUser();
    
    if (error) {
      throw error;
    }
    
    // Check for admin status in user metadata
    // This assumes admin status is stored in one of these locations
    const isAdmin = 
      user?.app_metadata?.is_admin === true || 
      user?.user_metadata?.is_admin === true || 
      false;
    
    return { isAdmin, error: null };
  } catch (error: any) {
    console.error('Error getting user admin status:', error);
    return { isAdmin: false, error };
  }
}

// Helper function to get user profile after login
export async function getUserProfile(userId: string) {
  try {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', userId)
      .single();

    if (error) {
      throw error;
    }

    // Get admin status from user metadata
    const { isAdmin } = await getUserAdminStatus();

    // Combine profile data with admin status
    return { 
      data: data ? { ...data, is_admin: isAdmin } : null, 
      error: null 
    };
  } catch (error: any) {
    console.error('Error fetching user profile:', error);
    return { data: null, error };
  }
}

// Helper function to create user profile if it doesn't exist
export async function createUserProfileIfNotExists(userId: string, email: string) {
  try {
    // Check if profile exists
    const { data: existingProfile } = await supabase
      .from('user_profiles')
      .select('id')
      .eq('id', userId)
      .single();

    // Get admin status from user metadata
    const { isAdmin } = await getUserAdminStatus();

    // If profile doesn't exist, create it
    if (!existingProfile) {
      const { data, error } = await supabase
        .from('user_profiles')
        .insert([
          {
            id: userId,
            email,
            subscription_tier: 'free',
            tokens_used_monthly: 0,
            tokens_monthly_limit: 100000,
          },
        ])
        .select()
        .single();

      if (error) throw error;
      
      // Return with admin status
      return { data: data ? { ...data, is_admin: isAdmin } : null, error: null };
    }

    return { 
      data: existingProfile ? { ...existingProfile, is_admin: isAdmin } : null, 
      error: null 
    };
  } catch (error: any) {
    console.error('Error creating user profile:', error);
    return { data: null, error };
  }
}

// Helper function to get session
export async function getSession() {
  const { data, error } = await supabase.auth.getSession();
  return { session: data.session, error };
}

export default supabase;